import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import adminService from '../services/adminService';
import { TrashIcon } from '@heroicons/react/24/outline';

// ADMINISTRACIÓ: qui entra i qui recupera la contrasenya.
//
// Dues cues, i totes dues existeixen perquè aquest servidor **no envia correu**. Sense correu no
// hi ha ni confirmació de registre ni enllaç de recuperació, o sigui que les dues coses passen
// per una persona: algú aprova el compte, i algú dicta el codi.
//
// Amagar aquesta pàgina a qui no és admin és comoditat, no seguretat: qualsevol pot demanar
// `/api/admin/...` a mà. Qui ho protegeix és el `router.use(authenticate, authorize('admin'))`
// del backend, que contesta 403 encara que aquesta pàgina no existís.

const ROLS = ['modeler', 'instructor', 'admin'];

const quan = (d) => (d ? new Date(d).toLocaleString('ca-ES', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
}) : '—');

// El codi, un cop generat, es veu UN sol cop. Aquest quadre és aquell cop: mentre és a la
// pantalla, es dicta. Si es tanca sense apuntar-lo, se n'ha de generar un altre.
function CodiGenerat({ dades, onTancar }) {
  const { t } = useTranslation();
  if (!dades) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-md shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900">{t('admin.codeFor', { name: dades.user?.name })}</h3>
        <p className="text-sm text-gray-500 mb-4">{dades.user?.email}</p>

        <div className="bg-gray-900 text-white rounded-md py-5 text-center mb-4">
          <span className="font-mono text-3xl tracking-[0.2em]">{dades.codi}</span>
        </div>

        <p className="text-sm text-warn-800 bg-warn-50 border border-warn-200 rounded-md p-3 mb-4">
          {t('admin.codeWarning', { when: quan(dades.expiresAt) })}
        </p>

        <button onClick={onTancar}
          className="w-full py-2.5 bg-primary-600 text-white rounded-md font-semibold hover:bg-primary-700">
          {t('admin.codeAcknowledge')}
        </button>
      </div>
    </div>
  );
}

const Admin = () => {
  const { user } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const [pendents, setPendents] = useState([]);
  const [resets, setResets] = useState([]);
  const [usuaris, setUsuaris] = useState([]);
  // Qui s'està a punt d'esborrar, i quants models té. El recompte no el sap el davant: el diu el
  // servidor quan es demana l'esborrat sense dir què fer-ne, i **no toca res** mentre no ho sàpigues.
  const [aEsborrar, setAEsborrar] = useState(null);
  // Els comptes que poden ser instructor d'algú. Surt de la mateixa llista d'usuaris: no cal
  // cap crida nova i queda al dia sol quan es canvia un rol.
  const docents = usuaris.filter((u) => ['instructor', 'admin'].includes(u.role));
  const [rolTriat, setRolTriat] = useState({});
  const [codi, setCodi] = useState(null);
  const [carregant, setCarregant] = useState(true);

  const carrega = useCallback(async () => {
    try {
      const [p, r, u] = await Promise.all([
        adminService.pending(), adminService.resets(), adminService.users()
      ]);
      setPendents(p.users || []);
      setResets(r.resets || []);
      setUsuaris(u.users || []);
    } catch (e) {
      toast.error(e.response?.data?.error || t('admin.loadFailed'));
    } finally { setCarregant(false); }
  }, []);

  useEffect(() => { carrega(); }, [carrega]);

  // Qui no és admin no hi pinta res. Es fa amb `Navigate` i no amagant el contingut perquè una
  // pàgina buida amb l'adreça posada fa pensar que s'ha trencat alguna cosa.
  if (user && user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const accio = async (fn, exit) => {
    try { await fn(); toast.success(exit); await carrega(); }
    catch (e) { toast.error(e.response?.data?.error || t('admin.actionFailed')); }
  };

  // PRIMERA CRIDA: sense dir què fer amb els models. **El servidor no esborra mai en aquesta
  // crida**, tingui feina o no: contesta 409 amb el recompte i espera. D'aquí surt el diàleg.
  //
  // Fins al 28/08/2026 aquesta crida SÍ que esborrava quan la persona no tenia cap model, i va
  // passar el que havia de passar: un compte se'n va anar amb un clic i cap pregunta.
  const provaEsborrar = async (u) => {
    try {
      await adminService.deleteUser(u.id);
      await carrega();   // no hi hauria d'arribar mai
    } catch (e) {
      const d = e.response?.data;
      if (d?.error === 'confirma_esborrat') return setAEsborrar({ ...u, models: d.models });
      toast.error(d?.error || t('admin.actionFailed'));
    }
  };

  const esborraDeBo = async (que) => {
    const u = aEsborrar;
    setAEsborrar(null);
    const clau = !u.models ? 'admin.compteEsborrat'
      : que === 'transfereix' ? 'admin.compteEsborratIModelsTeus'
      : 'admin.compteIModelsEsborrats';
    await accio(() => adminService.deleteUser(u.id, que), t(clau, { name: u.name, n: u.models }));
  };

  const generaCodi = async (fn) => {
    try {
      const d = await fn();
      setCodi(d);
      await carrega();
    } catch (e) { toast.error(e.response?.data?.error || t('admin.issueFailed')); }
  };

  if (carregant) return <div className="p-8 text-gray-500">{t('admin.loading')}</div>;

  const senseCodi = resets.filter((r) => !r.issuedAt);
  const ambCodi = resets.filter((r) => r.issuedAt);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-10">
      <CodiGenerat dades={codi} onTancar={() => setCodi(null)} />

      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">
          {t('admin.subtitle')}
        </p>
      </div>

      {/* ---------- comptes que esperen ---------- */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          {t('admin.waiting')} <span className="text-gray-500 font-normal">({pendents.length})</span>
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          {t('admin.waitingHint')}
        </p>

        {!pendents.length && (
          <p className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-md p-6 text-center">
            {t('admin.nobodyWaiting')}
          </p>
        )}

        <div className="space-y-2">
          {pendents.map((u) => (
            <div key={u.id} className="border border-gray-200 rounded-md p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[220px]">
                <div className="font-medium text-gray-900">{u.name}</div>
                <div className="text-sm text-gray-500">{u.email}</div>
                {/* Aquí hi deia «demana: Modelador». Des del 27/08/2026 el registre no pregunta
                    cap rol i tothom entra com a modelador, o sigui que la línia deia sempre el
                    mateix: soroll a la pantalla on es pren la decisió de debò, i que a més
                    convida a fer cas d'una petició que ja no existeix. */}
                <div className="text-xs text-gray-500 mt-1">
                  {t('admin.askedOn', { when: quan(u.createdAt) })}
                </div>
              </div>
              <select
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={rolTriat[u.id] || u.requestedRole || 'modeler'}
                onChange={(e) => setRolTriat({ ...rolTriat, [u.id]: e.target.value })}>
                {ROLS.map((r) => <option key={r} value={r}>{t(`roles.${r}`)}</option>)}
              </select>
              <button
                onClick={() => accio(
                  () => adminService.approve(u.id, rolTriat[u.id] || u.requestedRole || 'modeler'),
                  t('admin.approved', { name: u.name }))}
                className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md font-medium hover:bg-primary-700">
                {t('admin.approve')}
              </button>
              <button
                onClick={() => accio(() => adminService.reject(u.id), t('admin.rejected'))}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50">
                {t('admin.reject')}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- contrasenyes ---------- */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          {t('admin.resets')} <span className="text-gray-500 font-normal">({senseCodi.length})</span>
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          {t('admin.resetsHint')}
        </p>

        {!resets.length && (
          <p className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-md p-6 text-center">
            {t('admin.noResets')}
          </p>
        )}

        <div className="space-y-2">
          {senseCodi.map((r) => (
            <div key={r.id} className="border border-gray-200 rounded-md p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[220px]">
                <div className="font-medium text-gray-900">{r.user?.name}</div>
                <div className="text-sm text-gray-500">{r.user?.email}</div>
                <div className="text-xs text-gray-500 mt-1">{t('admin.askedOn', { when: quan(r.requestedAt) })}</div>
              </div>
              <button onClick={() => generaCodi(() => adminService.issue(r.id))}
                className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md font-medium hover:bg-primary-700">
                {t('admin.issue')}
              </button>
              <button onClick={() => accio(() => adminService.dismiss(r.id), t('admin.dismissed'))}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50">
                {t('admin.dismiss')}
              </button>
            </div>
          ))}

          {ambCodi.map((r) => (
            <div key={r.id} className="border border-gray-100 bg-gray-50 rounded-md p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[220px]">
                <div className="text-gray-700">{r.user?.name} <span className="text-gray-500">· {r.user?.email}</span></div>
                <div className="text-xs text-gray-500 mt-1">
                  {r.caducat
                    ? <span className="text-warn-700">{t('admin.codeExpired', { when: quan(r.expiresAt) })}</span>
                    : t('admin.codeLive', { issued: quan(r.issuedAt), expires: quan(r.expiresAt) })}
                </div>
              </div>
              <button onClick={() => generaCodi(() => adminService.issue(r.id))}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-white">
                {t('admin.issueAnother')}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- tothom ---------- */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('admin.everyone')} <span className="text-gray-500 font-normal">({usuaris.length})</span>
        </h2>
        <div className="border border-gray-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('admin.colName')}</th>
                <th className="px-4 py-2 font-medium">{t('admin.colStatus')}</th>
                <th className="px-4 py-2 font-medium">{t('admin.colRole')}</th>
                <th className="px-4 py-2 font-medium">{t('admin.colInstructor')}</th>
                <th className="px-4 py-2 font-medium">{t('admin.colLastSignIn')}</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {usuaris.map((u) => (
                <tr key={u.id} className="border-t border-gray-100">
                  <td className="px-4 py-2">
                    <div className="text-gray-900">{u.name}</div>
                    <div className="text-gray-500 text-xs">{u.email}</div>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-1 rounded-md ${
                      u.approvalStatus === 'approved' ? 'bg-ok-50 text-ok-800'
                        : u.approvalStatus === 'pending' ? 'bg-warn-50 text-warn-800'
                        : 'bg-gray-100 text-gray-600'}`}>
                      {u.approvalStatus}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <select
                      className="border border-gray-200 rounded-md px-2 py-1 text-sm"
                      value={u.role}
                      disabled={u.approvalStatus !== 'approved'}
                      onChange={(e) => accio(() => adminService.setRole(u.id, e.target.value),
                        t('admin.roleChanged', { name: u.name, role: t(`roles.${e.target.value}`) }))}>
                      {ROLS.map((r) => <option key={r} value={r}>{t(`roles.${r}`)}</option>)}
                    </select>
                  </td>
                  {/* DE QUIN DOCENT ÉS. Sense això, `instructor_id` és una columna que no
                      omple ningú i la meitat de la regla de visibilitat no s'aplica mai. */}
                  <td className="px-4 py-2">
                    <select
                      className="border border-gray-200 rounded-md px-2 py-1 text-sm max-w-[150px]"
                      value={u.instructorId || ''}
                      disabled={u.approvalStatus !== 'approved'}
                      onChange={(e) => accio(
                        () => adminService.setInstructor(u.id, e.target.value || null),
                        t('admin.instructorChanged', { name: u.name }))}>
                      <option value="">{t('admin.senseInstructor')}</option>
                      {docents.filter((d) => d.id !== u.id).map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-2 text-gray-500">{quan(u.lastLogin)}</td>
                  <td className="px-4 py-2 text-right">
                    {/* Per a l'aula: algú s'ha quedat fora i et té al davant. Fer-lo passar
                        per la pantalla de «no puc entrar» per tornar aquí és una volta que
                        no cal. */}
                    <div className="flex items-center justify-end gap-3">
                      {u.approvalStatus === 'approved' && (
                        <button onClick={() => generaCodi(() => adminService.resetFor(u.id))}
                          className="text-primary-600 hover:text-primary-800 text-sm">
                          {t('admin.newPassword')}
                        </button>
                      )}
                      {/* No hi surt al teu propi compte: esborrar-te a tu mateix deixaria TRAMA
                          sense ningú que pugui aprovar res, i el servidor també ho refusa. */}
                      {u.id !== user?.id && (
                        <button onClick={() => provaEsborrar(u)}
                          title={t('admin.esborraCompte')} aria-label={t('admin.esborraCompte')}
                          className="p-1 rounded-md text-gray-400 hover:text-danger-700 hover:bg-danger-50">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ESBORRAR UN COMPTE QUE TÉ FEINA DESADA.
          El diàleg no pregunta «segur?»: **diu quants models se'n van** i dona la sortida que no
          perd res. `models.creator_id` és ON DELETE CASCADE, o sigui que la via destructiva ho
          és de debò i no té marxa enrere. */}
      {aEsborrar && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-md p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('admin.esborrarTitol', { name: aEsborrar.name })}
            </h3>
            <p className="mt-2 text-sm text-gray-700">
              {aEsborrar.models
                ? t('admin.esborrarTeModels', { count: aEsborrar.models })
                : t('admin.esborrarSenseModels')}
            </p>
            {/* Encara que no tingui cap model, esborrar no té marxa enrere: els alumnes que
                el tinguessin com a instructor es queden sense, i això no ho diu res més. */}
            {!aEsborrar.models && (
              <p className="mt-2 text-sm text-gray-600">{t('admin.esborrarConseqüencia')}</p>
            )}
            {!!aEsborrar.models && (
              <p className="mt-2 text-sm text-gray-600">{t('admin.esborrarTria')}</p>
            )}

            <div className="mt-5 space-y-2">
              {!aEsborrar.models && (
                <button type="button" onClick={() => esborraDeBo('esborra')}
                  className="w-full text-left p-3 border border-danger-200 rounded-md hover:bg-danger-50">
                  <span className="block text-sm font-medium text-danger-800">
                    {t('admin.esborraCompte')}
                  </span>
                </button>
              )}
              {!!aEsborrar.models && (<>
              <button type="button" onClick={() => esborraDeBo('transfereix')}
                className="w-full text-left p-3 border border-gray-200 rounded-md hover:bg-gray-50">
                <span className="block text-sm font-medium text-gray-900">
                  {t('admin.esborrarTransfereix')}
                </span>
                <span className="block text-sm text-gray-500 mt-0.5">
                  {t('admin.esborrarTransfereixNota')}
                </span>
              </button>
              <button type="button" onClick={() => esborraDeBo('esborra')}
                className="w-full text-left p-3 border border-danger-200 rounded-md hover:bg-danger-50">
                <span className="block text-sm font-medium text-danger-800">
                  {t('admin.esborrarTot', { count: aEsborrar.models })}
                </span>
                <span className="block text-sm text-danger-700 mt-0.5">
                  {t('admin.esborrarTotNota')}
                </span>
              </button>
              </>)}
            </div>

            <div className="mt-5 flex justify-end">
              <button type="button" onClick={() => setAEsborrar(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
